import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, TrendingUp, Settings, MapPin, CheckCircle, RotateCcw } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Navbar from '../components/Navbar';
import BrokerAvatarUpload from '../components/BrokerAvatarUpload';
import ConfirmModal from '../components/ConfirmModal';
import { toLanguageArray } from '../lib/languages';
import './AgentDashboard.css';
import '../styles/userPages.css';

function formatPrice(price, priceType) {
  if (!price) return '—';
  if (price >= 1_000_000) return `€${(price / 1_000_000).toFixed(1)}M`;
  if (price >= 1000) return `€${Math.round(price / 1000)}k`;
  return `€${price.toLocaleString('en-GB')}`;
}

export default function AgentDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('listings');
  const [listings, setListings] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [markSoldId, setMarkSoldId] = useState(null);
  const [unmarkSoldId, setUnmarkSoldId] = useState(null);
  const [deleteListingId, setDeleteListingId] = useState(null);

  const stats = {
    activeListings: listings.filter(l => !l.status || l.status === 'active').length,
    totalViews: listings.reduce((sum, l) => sum + (l.views_count || 0), 0),
    boostedListings: listings.filter(l => l.is_boosted).length,
  };

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    loadData();
  }, [user]); // eslint-disable-line

  async function loadData() {
    setLoading(true);

    // Best-effort: archive any of this user's listings that have been "sold" for >4h.
    // Safe even if the status column doesn't exist yet — a 400 here just skips silently.
    const fourHoursAgo = new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString();
    await supabase
      .from('listings')
      .update({ status: 'archived' })
      .eq('user_id', user.id)
      .eq('status', 'sold')
      .lt('sold_at', fourHoursAgo);

    const [{ data: listingsData }, { data: profileData }] = await Promise.all([
      supabase.from('listings').select('*').eq('user_id', user.id).neq('status', 'deleted').order('created_at', { ascending: false }),
      supabase.from('profiles').select('*').eq('id', user.id).single(),
    ]);
    setListings(listingsData || []);
    setProfile(profileData);
    setLoading(false);
  }

  async function confirmMarkSold() {
    const listingId = markSoldId;
    setMarkSoldId(null);
    if (!listingId) return;
    const { error } = await supabase
      .from('listings')
      .update({ status: 'sold', sold_at: new Date().toISOString() })
      .eq('id', listingId)
      .eq('user_id', user.id);
    if (error) { alert(`Failed to mark as sold: ${error.message}`); return; }
    loadData();
  }

  async function confirmUnmarkSold() {
    const listingId = unmarkSoldId;
    setUnmarkSoldId(null);
    if (!listingId) return;
    const { error } = await supabase
      .from('listings')
      .update({ status: 'active', sold_at: null })
      .eq('id', listingId)
      .eq('user_id', user.id);
    if (error) { alert(`Failed to restore: ${error.message}`); return; }
    loadData();
  }

  async function confirmDeleteListing() {
    const listingId = deleteListingId;
    setDeleteListingId(null);
    if (!listingId) return;
    // Soft-delete so existing buyer/seller chats survive (see MyListingsPage).
    const { error } = await supabase
      .from('listings')
      .update({ status: 'deleted', deleted_at: new Date().toISOString() })
      .eq('id', listingId);
    if (!error) setListings(prev => prev.filter(l => l.id !== listingId));
  }

  const displayName = profile?.full_name || user?.email?.split('@')[0] || t('nav.agent');
  const avatarLetter = displayName.charAt(0).toUpperCase();

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="agentDashboard__loading">{t('agentDashboard.loading')}</div>
      </>
    );
  }

  return (
    <div className="userPage">
      <Navbar />

      <div className="userPage__body">
        {/* Sidebar */}
        <aside className="userPage__sidebar">
          <div className="userPage__avatar">{avatarLetter}</div>
          <div className="userPage__name">{displayName}</div>
          <div className="userPage__email">{user?.email}</div>
          <div className="userPage__statsBadge">
            <span>{stats.activeListings} listings</span>
            {stats.boostedListings > 0 && <span>{stats.boostedListings} boosted</span>}
          </div>
          <nav className="userPage__nav">
            <button className="userPage__navItem" onClick={() => navigate('/profile')}>{t('nav.myProfile')}</button>
            <button className="userPage__navItem userPage__navItem--active" onClick={() => navigate('/dashboard')}>{t('nav.myListings')}</button>
            <button className="userPage__navItem" onClick={() => navigate('/inbox')}>{t('nav.inbox')}</button>
            <button className="userPage__navItem" onClick={() => navigate('/settings')}>{t('nav.settings')}</button>
            <button className="userPage__navItem userPage__navItem--danger" onClick={() => { logout(); navigate('/'); }}>{t('nav.logout')}</button>
          </nav>
        </aside>

        {/* Content */}
        <div className="userPage__content">
          <div className="userPage__pageHeader">
            <h1 className="userPage__pageTitle">{t('agentDashboard.pageTitle')}</h1>
            <p className="userPage__pageSub">
              {t('agentDashboard.pageSubViews')
                .replace('{views}', String(stats.totalViews))
                .replace('{active}', String(stats.activeListings))}
            </p>
          </div>

          <nav className="agentDashboard__tabs">
            {[
              { id: 'listings', icon: <Eye size={16} />, label: t('agentDashboard.tabs.listings') },
              { id: 'profile',  icon: <Settings size={16} />, label: t('agentDashboard.tabs.agentProfile') },
            ].map(tab => (
              <button
                key={tab.id}
                className={`agentDashboard__tab ${activeTab === tab.id ? 'is-active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </nav>

          <div className="agentDashboard__content">
            {activeTab === 'listings' && (
              <ListingsTab
                listings={listings}
                onDelete={(id) => setDeleteListingId(id)}
                onMarkSold={(id) => setMarkSoldId(id)}
                onUnmarkSold={(id) => setUnmarkSoldId(id)}
                navigate={navigate}
              />
            )}
            {activeTab === 'profile' && <ProfileTab profile={profile} user={user} onUpdate={loadData} />}
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={markSoldId !== null}
        title={t('agentDashboard.markSold.title')}
        message={t('agentDashboard.markSold.message')}
        confirmLabel={t('agentDashboard.markSold.confirm')}
        cancelLabel={t('common.cancel')}
        onConfirm={confirmMarkSold}
        onCancel={() => setMarkSoldId(null)}
      />

      <ConfirmModal
        isOpen={unmarkSoldId !== null}
        title={t('agentDashboard.restore.title')}
        message={t('agentDashboard.restore.message')}
        confirmLabel={t('agentDashboard.restore.confirm')}
        cancelLabel={t('common.cancel')}
        variant="danger"
        onConfirm={confirmUnmarkSold}
        onCancel={() => setUnmarkSoldId(null)}
      />

      <ConfirmModal
        isOpen={deleteListingId !== null}
        title={t('agentDashboard.delete.title')}
        message={t('agentDashboard.delete.message')}
        confirmLabel={t('agentDashboard.delete.confirm')}
        cancelLabel={t('common.cancel')}
        variant="danger"
        onConfirm={confirmDeleteListing}
        onCancel={() => setDeleteListingId(null)}
      />
    </div>
  );
}

function hoursUntilArchive(soldAt) {
  if (!soldAt) return 0;
  const archiveAt = new Date(soldAt).getTime() + 4 * 60 * 60 * 1000;
  return Math.max(0, Math.ceil((archiveAt - Date.now()) / (60 * 60 * 1000)));
}

function ListingsTab({ listings, onDelete, onMarkSold, onUnmarkSold, navigate }) {
  if (listings.length === 0) {
    return (
      <div className="agentDashboard__empty">
        <h3>No listings yet</h3>
        <p>Start by adding your first property listing.</p>
        <Link to="/list-property" className="agentDashboard__primaryBtn">
          <Plus size={16} /> Add New Listing
        </Link>
      </div>
    );
  }

  return (
    <div className="agentDashboard__listings">
      <div className="agentDashboard__listingsHeader">
        <h2>My Listings</h2>
        <Link to="/list-property" className="agentDashboard__primaryBtn">
          <Plus size={16} /> New Listing
        </Link>
      </div>
      <div className="agentDashboard__listingsRows">
        {listings.map(listing => {
          const isSold = listing.status === 'sold';
          const isArchived = listing.status === 'archived';
          return (
          <article key={listing.id} className={`dlRow${isSold ? ' dlRow--sold' : ''}${isArchived ? ' dlRow--archived' : ''}`}>
            <div className="dlRow__imgWrap">
              <img
                src={listing.images?.[0] || 'https://placehold.co/90x68/f3f4f6/9ca3af?text=No+Image'}
                alt={listing.title}
                className="dlRow__img"
              />
              {isSold && (
                <div className="dlRow__soldBanner"><CheckCircle size={12} /> SOLD</div>
              )}
            </div>
            <div className="dlRow__info">
              <div className="dlRow__title">{listing.title}</div>
              <div className="dlRow__location"><MapPin size={11} /> {listing.area || listing.city}</div>
              <div className="dlRow__meta">
                <span><Eye size={11} /> {listing.views_count || 0}</span>
                {isSold && (
                  <span className="dlRow__statusChip dlRow__statusChip--sold">
                    SOLD <span className="dlRow__statusMeta">(auto-archive in {hoursUntilArchive(listing.sold_at)}h)</span>
                  </span>
                )}
                {isArchived && (
                  <span className="dlRow__statusChip dlRow__statusChip--archived">ARCHIVED</span>
                )}
              </div>
            </div>
            <div className="dlRow__price">
              {formatPrice(listing.price)}{listing.price_label === 'per month' && <span>/mo</span>}
            </div>
            <div className="dlRow__actions">
              <Link to={`/property/${listing.id}`} className="dashboardListing__btn dashboardListing__btn--secondary">View</Link>
              <Link to={`/edit-listing/${listing.id}`} className="dashboardListing__btn dashboardListing__btn--secondary"><Edit size={12} /> Edit</Link>
              <Link to={`/boost-checkout?listings=${listing.id}`} className="dashboardListing__btn dashboardListing__btn--primary"><TrendingUp size={12} /> Boost</Link>
              {isSold || isArchived ? (
                <button
                  onClick={() => onUnmarkSold(listing.id)}
                  className="dashboardListing__btn dashboardListing__btn--secondary"
                  aria-label="Restore listing"
                  title="Restore"
                >
                  <RotateCcw size={12} /> Restore
                </button>
              ) : (
                <button
                  onClick={() => onMarkSold(listing.id)}
                  className="dashboardListing__btn dashboardListing__btn--secondary"
                  aria-label="Mark as sold"
                  title="Mark as Sold"
                >
                  <CheckCircle size={12} /> Mark Sold
                </button>
              )}
              <button
                onClick={() => onDelete(listing.id)}
                className="dashboardListing__btn dashboardListing__btn--danger"
                aria-label="Delete"
                title="Delete"
              >
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </article>
        );
        })}
      </div>
    </div>
  );
}


function ProfileTab({ profile, user, onUpdate }) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [formData, setFormData] = useState({
    full_name:    profile?.full_name    || '',
    broker_phone: profile?.broker_phone || '',
    broker_city:  profile?.broker_city  || '',
    speciality:   profile?.speciality   || '',
    bio:          profile?.bio          || '',
  });
  const [languages, setLanguages]         = useState(() => toLanguageArray(profile?.languages));
  const [languageInput, setLanguageInput] = useState('');

  function addLanguage() {
    const next = languageInput.trim();
    if (!next || languages.some(l => l.toLowerCase() === next.toLowerCase())) {
      setLanguageInput('');
      return;
    }
    setLanguages([...languages, next]);
    setLanguageInput('');
  }

  function removeLanguage(lang) {
    setLanguages(languages.filter(l => l !== lang));
  }

  async function handleSave() {
    setSaving(true);

    const profilePayload = {
      full_name: formData.full_name,
      bio:       formData.bio,
    };
    if (profile?.avatar_url !== undefined) profilePayload.avatar_url = profile.avatar_url;

    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .update(profilePayload)
      .eq('id', user.id)
      .select()
      .single();

    if (profileError) {
      setSaving(false);
      alert(`Save failed: ${profileError.message}\n\nField that failed: ${profileError.details || 'unknown'}`);
      return;
    }

    if (profile?.is_broker && user?.email) {
      const brokerPayload = {
        phone:       formData.broker_phone,
        city:        formData.broker_city,
        speciality:  formData.speciality,
        description: formData.bio,
        languages,
      };
      const { error: brokerError } = await supabase
        .from('brokers')
        .update(brokerPayload)
        .eq('email', user.email);

      if (brokerError) {
        setSaving(false);
        alert(`Broker info save failed: ${brokerError.message}\n\nField that failed: ${brokerError.details || 'unknown'}`);
        return;
      }
    }

    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
    if (onUpdate) onUpdate(profileData);
  }

  return (
    <div className="agentDashboard__profile">
      <div className="agentDashboard__profileHeader">
        <h2>Profile Settings</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {saved && <span style={{ fontSize: 12, color: '#059669', fontWeight: 600 }}>✓ Saved</span>}
          <button onClick={handleSave} className="agentDashboard__primaryBtn" disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="agentDashboard__profileAvatarWrap">
        <BrokerAvatarUpload
          currentAvatarUrl={profile?.avatar_url}
          userId={user?.id}
          brokerId={null}
          userEmail={user?.email}
          onUploadComplete={() => onUpdate()}
        />
      </div>

      <div className="agentDashboard__profileFields">
        {[
          { key: 'full_name',   label: 'Full name', type: 'text' },
          { key: 'broker_phone', label: 'Phone', type: 'tel' },
          { key: 'broker_city', label: 'City', type: 'text' },
          { key: 'speciality',  label: 'Speciality (comma-separated)', type: 'text', placeholder: 'e.g. Residential, Luxury' },
        ].map(f => (
          <label key={f.key}>
            {f.label}
            <input
              type={f.type}
              placeholder={f.placeholder || ''}
              value={formData[f.key]}
              onChange={e => setFormData(p => ({ ...p, [f.key]: e.target.value }))}
            />
          </label>
        ))}

        <label className="agentDashboard__langField">
          Languages
          <div className="agentDashboard__chipList">
            {languages.map(lang => (
              <span key={lang} className="agentDashboard__chip">
                {lang}
                <button
                  type="button"
                  onClick={() => removeLanguage(lang)}
                  aria-label={`Remove ${lang}`}
                  className="agentDashboard__chipRemove"
                >×</button>
              </span>
            ))}
          </div>
          <div className="agentDashboard__chipInputRow">
            <input
              type="text"
              value={languageInput}
              onChange={e => setLanguageInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLanguage(); } }}
              placeholder="Type a language and press Enter"
            />
            <button
              type="button"
              onClick={addLanguage}
              className="agentDashboard__chipAddBtn"
              disabled={!languageInput.trim()}
            >+ Add</button>
          </div>
        </label>

        <label>
          Bio
          <textarea
            value={formData.bio}
            onChange={e => setFormData(p => ({ ...p, bio: e.target.value }))}
            maxLength={300}
            placeholder="Tell buyers about yourself…"
          />
        </label>
      </div>
      {profile?.is_broker && (
        <div className="agentDashboard__verifiedBadge">
          ✓ Verified Agent
          <small>Your credentials have been verified by CosmoEstates.</small>
        </div>
      )}
    </div>
  );
}
